/**
 * Tiền tệ theo công ty và tỷ giá quy đổi đơn giá sản phẩm (yêu cầu v1.8 mục 6.2–6.4, 11/10/2026).
 *
 * - Bốn tiền tệ Hisense dùng: USD, HKD, RMB, VND (đúng danh sách đang lưu trên production). Mã "RMB" như dữ liệu đang có, không đổi
 *   sang mã ISO "CNY".
 * - Sáu cặp, MỖI CẶP MỘT TỶ GIÁ có chiều rõ ràng: "1 USD = 6.6 RMB". Chiều ngược suy ra từ cùng con số (RMB → USD = chia 6.6), đúng
 *   công thức khách ghi: "USD unit price = RMB unit price ÷ 6.6; RMB unit price = USD unit price × 6.6". Như bảng tỷ giá của ERP
 *   chuẩn (Odoo `res.currency.rate`, SAP TCURR): một con số cho một cặp, không nhập hai chiều lệch nhau.
 * - Phép tính bằng SỐ NGUYÊN (BigInt) trên đơn vị nhỏ nhất `MONEY_SCALE`: không có sai số dấu phẩy động trong tiền. Kết quả làm tròn
 *   nửa lên về 4 chữ số lẻ — đúng độ phân giải lưu đơn giá của hệ (golden rule 6). Số chưa làm tròn vẫn trả về để màn hình cho xem.
 * - Không bao giờ tự quy đổi: chỉ chạy khi người lập PI tick "Quy đổi đơn giá" và bấm áp dụng. Tỷ giá đổi về sau không tính lại PI
 *   đã lưu — PI giữ tiền gốc, giá gốc, tỷ giá đã áp và kết quả trong bản chụp dòng (`LinePriceSnapshot.fx`).
 */
import { MONEY_DP, MONEY_SCALE } from "./money";

export const FX_CURRENCIES = ["USD", "HKD", "RMB", "VND"] as const;
export type FxCurrency = (typeof FX_CURRENCIES)[number];

/** Sáu cặp, tiền đứng trước là "1 đơn vị" của tỷ giá: 1 USD = … HKD / RMB / VND; 1 HKD = … RMB / VND; 1 RMB = … VND. */
export const FX_PAIRS: readonly (readonly [FxCurrency, FxCurrency])[] = [
  ["USD", "HKD"],
  ["USD", "RMB"],
  ["USD", "VND"],
  ["HKD", "RMB"],
  ["HKD", "VND"],
  ["RMB", "VND"],
];

/** Tiền tệ mặc định theo công ty khi admin chưa lưu gì: Trung Quốc RMB, Việt Nam USD (khách ghi ở mục 6.2). */
export const DEFAULT_CURRENCY_BY_REGION: Readonly<Record<"vietnam" | "china", string>> = { vietnam: "USD", china: "RMB" };

/** Một tỷ giá: 1 `base` = `rate` `quote`. `rate` là chuỗi thập phân dương, tối đa 8 chữ số lẻ. */
export interface FxRate {
  base: FxCurrency;
  quote: FxCurrency;
  rate: string;
  /** Lúc tỷ giá này được đặt lần cuối (máy chủ ghi). */
  updatedAt?: number | null;
}

export const FX_RATE_PATTERN = /^\d{1,12}(\.\d{1,8})?$/;

export const isFxCurrency = (code: string | null | undefined): code is FxCurrency =>
  !!code && (FX_CURRENCIES as readonly string[]).includes(code);

/**
 * "6.600" → "6.6"; chuỗi không hợp lệ hoặc ≤ 0 → null.
 *
 * KHÔNG nhận dấu phẩy (kiểm thử 11/10/2026): trước đây phẩy bị xoá như dấu nghìn, nên người Việt gõ "6,6" lưu thành 66 — lệch 10 lần
 * mà không báo gì. Phẩy vừa là dấu thập phân (vi) vừa là dấu nghìn (en) nên không đoán được; chỉ nhận dấu chấm thập phân, ô nhập báo
 * rõ. (Dấu chấm vẫn có thể bị gõ làm dấu nghìn kiểu Việt — "25.400" — nên Cài đặt hiện chiều ngược và cảnh báo khi tỷ giá đổi mạnh.)
 */
export function normalizeRate(raw: string | null | undefined): string | null {
  const v = (raw ?? "").trim();
  if (!FX_RATE_PATTERN.test(v)) return null;
  const [int = "0", frac = ""] = v.split(".");
  const fracTrim = frac.replace(/0+$/, "");
  const intTrim = int.replace(/^0+(?=\d)/, "");
  const out = fracTrim ? `${intTrim}.${fracTrim}` : intTrim;
  return /[1-9]/.test(out) ? out : null;
}

/** Tỷ giá đang đặt cho cặp (chiều nào cũng tìm ra), hoặc null khi chưa đặt. */
export function findRate(rates: readonly FxRate[] | undefined, a: string, b: string): FxRate | null {
  if (!rates) return null;
  return rates.find((r) => (r.base === a && r.quote === b) || (r.base === b && r.quote === a)) ?? null;
}

/** "1 USD = 6.6 RMB" — luôn ghi đủ hai tiền tệ, con số và chiều (khách: "not just 1:6.6"). */
export function rateLabel(rate: { base: string; quote: string; rate: string }): string {
  return `1 ${rate.base} = ${rate.rate} ${rate.quote}`;
}

function rateFraction(rate: string): { num: bigint; den: bigint } {
  const [int = "0", frac = ""] = rate.split(".");
  return { num: BigInt(`${int}${frac}` || "0"), den: 10n ** BigInt(frac.length) };
}

/** a / b làm tròn nửa lên, a ≥ 0, b > 0. */
function divRound(a: bigint, b: bigint): bigint {
  return (a * 2n + b) / (2n * b);
}

export interface FxConversionResult {
  /** Đơn giá sau quy đổi, đơn vị nhỏ nhất (4 chữ số lẻ, làm tròn nửa lên). */
  minor: number;
  /** Cùng số đó CHƯA làm tròn, 6 chữ số lẻ, để người lập thấy độ chính xác thật (RMB 0.32 ÷ 6.6 = 0.048485). */
  exact: string;
  /** Tỷ giá đã dùng — null khi hai tiền tệ trùng nhau (giá giữ nguyên). */
  rate: FxRate | null;
}

/**
 * Quy đổi một đơn giá (đơn vị nhỏ nhất) từ `from` sang `to` theo bảng tỷ giá. Cùng tiền tệ → giữ nguyên. Thiếu tỷ giá cho cặp → null
 * (màn hình phải nhắc đặt tỷ giá trước, không đoán).
 */
export function convertUnitPrice(
  amountMinor: number,
  from: string,
  to: string,
  rates: readonly FxRate[] | undefined,
): FxConversionResult | null {
  if (!Number.isSafeInteger(amountMinor) || amountMinor < 0) return null;
  if (from === to) return { minor: amountMinor, exact: exactString(BigInt(amountMinor) * 100n, 1n), rate: null };
  const rate = findRate(rates, from, to);
  const norm = rate ? normalizeRate(rate.rate) : null;
  if (!rate || !norm) return null;
  // Tính trên chuỗi ĐÃ chuẩn hoá — chuỗi thô có thể còn khoảng trắng / số 0 thừa.
  const { num, den } = rateFraction(norm);
  if (num <= 0n) return null;
  const amount = BigInt(amountMinor);
  // from = base: nhân tỷ giá; from = quote: chia tỷ giá.
  const [n, d] = rate.base === from ? [amount * num, den] : [amount * den, num];
  const q = divRound(n, d);
  // Kết quả vượt số nguyên an toàn thì Number() làm mất chữ số — từ chối thay vì lưu một đơn giá sai.
  if (q > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  return { minor: Number(q), exact: exactString(n * 100n, d), rate };
}

/** n / d đơn vị nhỏ nhất × 100 → chuỗi đơn vị chính, 6 chữ số lẻ (MONEY_DP + 2). */
function exactString(n: bigint, d: bigint): string {
  const scaled = divRound(n, d); // đơn vị 1e-(MONEY_DP+2)
  const unit = BigInt(MONEY_SCALE) * 100n;
  const whole = scaled / unit;
  const frac = (scaled % unit).toString().padStart(MONEY_DP + 2, "0");
  return `${whole}.${frac}`;
}

/** Tiền tệ mặc định của PI / sản phẩm mới theo công ty (khu vực dữ liệu của khách hoặc của người lập). */
export function defaultCurrencyFor(
  region: "vietnam" | "china" | null | undefined,
  byRegion: Partial<Record<"vietnam" | "china", string>> | undefined,
  fallback: string,
): string {
  if (!region) return fallback;
  return byRegion?.[region] || DEFAULT_CURRENCY_BY_REGION[region];
}
